/**
 * The advisor data service is not deployed (operator ruling 2026-10-12, answer 4
 * of KG record 2026-10-12-rob-s-eight-answers-of-2026-10-12-second-set.md), so
 * the pages that read it say so instead of reporting a failure (operator ruling
 * 2026-10-16).
 *
 * advisor.aaim.com sends /api/* to this SPA's nginx, apart from its PE, connector
 * and notification path rules. The nginx /api/ catch-all's upstream is
 * api-gateway, which is not deployed (ruling D7), so ADVISOR_TOOLS_API_URL is
 * empty and the catch-all answers 404 {"error":"route_not_found"} with no
 * upstream call. A 503 means the same. Any other failure keeps the page's own
 * error.
 */

import { Alert } from '@aaimplatform/ui';

export const ADVISOR_SERVICE_MISSING_TITLE = 'The advisor data service is not available';

export class AdvisorServiceMissingError extends Error {
  constructor() {
    super(ADVISOR_SERVICE_MISSING_TITLE);
    this.name = 'AdvisorServiceMissingError';
  }
}

export function isAdvisorServiceMissing(response: { status: number } | undefined, error: unknown): boolean {
  const status = response?.status;
  if (status === 503) return true;
  if (status !== 404 || typeof error !== 'object' || error === null) return false;
  return (error as { error?: unknown }).error === 'route_not_found';
}

/** The error to throw for a failed advisor call: the missing-service error, or a plain one with `fallback`. */
export function advisorFailure(response: { status: number } | undefined, error: unknown, fallback: string): Error {
  return isAdvisorServiceMissing(response, error) ? new AdvisorServiceMissingError() : new Error(fallback);
}

/** advisorFailure for a raw fetch() response, whose body is read only on a 404. */
export async function advisorFetchFailure(response: Response, fallback: string): Promise<Error> {
  let body: unknown = null;
  if (response.status === 404) {
    try {
      body = await response.clone().json();
    } catch {
      body = null;
    }
  }
  return advisorFailure(response, body, fallback);
}

export function AdvisorServiceNotice({ className }: { className?: string }) {
  return (
    <Alert type="warning" title={ADVISOR_SERVICE_MISSING_TITLE} className={className}>
      The advisor data service did not answer this page&apos;s requests, so its data cannot be shown here.
    </Alert>
  );
}
